import { origemAtual } from "@/lib/navegacao";
import { Link } from "wouter";
import { Badge } from "@/components/ui/badge";
import type { MensalistaDaQuinzena } from "@/hooks/use-equipe";
import {
  formatarBRL, formatarDataCurta, dataPagamentoDaCompetencia,
  competenciaDaQuinzena, calcLiquidoFechamento, proximaCompetencia,
  normalizarDatasISO, formatarResumoAdicional, paraCentavos, paraReais,
  totalAdicionaisCentavos as somarLinhasAdicional, type AdicionalResumoPorTipo,
  calcProjecaoAdiantamentoPendente, temAdiantamentoNaCompetencia,
} from "@shared/equipe-fechamento";

/** "1 dia" / "2 dias" — concordância, não "1 dias". */
function plural(n: number, singular: string, pluralForm: string): string {
  return `${n} ${n === 1 ? singular : pluralForm}`;
}

interface Props {
  linhas: MensalistaDaQuinzena[];
  /** Dias do período, em ISO, na ordem — a mesma faixa da grade dos diaristas. */
  dias: string[];
  /** `periodo_inicio` da quinzena (YYYY-MM-DD) — de onde a competência (D11) vem. */
  periodoInicio: string;
  diaPagamentoMensal: number | null;
  /** Fase 3 — agregado por funcionário (`l.id`), uma linha por tipo (§2.2). */
  adicionalPorFuncionario?: Map<string, AdicionalResumoPorTipo[]>;
}

function nomeDe(l: MensalistaDaQuinzena) {
  return l.apelido ?? l.nome;
}

/**
 * §4.2 bloco 2 — Mensalistas e encarregados.
 *
 * ⚠️ AS CÉLULAS DE DIA NUNCA MOSTRAM R$. Só `✓` (registro) e `falta`.
 * Mensalista não tem valor de dia; um "R$ 0,00" na grade é lido como falta — é
 * exatamente o bug visual que este bloco existe para matar. O único dinheiro que
 * aparece aqui é o adiantamento, e ele vem de tabela própria, nunca de `diarias`.
 *
 * Os dados vêm de `/api/equipe/quinzenas/:id/mensalistas`, endpoint SEPARADO da
 * folha (§4.5): não existe caminho desta tela até o total pago da quinzena.
 *
 * D13 v2.9 — não tem botão de marcar adiantamento como pago aqui. O único
 * caminho pra `PREVISTO` virar `PAGO` é fechar a quinzena (cascata em
 * `fecharQuinzena`, PR #120) — decisão deliberada por simplicidade, ainda que
 * isso signifique todo mundo com o mesmo `pago_em` (data de pagamento da
 * quinzena), não o dia exato em que cada um foi pago de fato.
 */
export function BlocoMensalistas({
  linhas, dias, periodoInicio, diaPagamentoMensal,
  adicionalPorFuncionario,
}: Props) {
  if (linhas.length === 0) return null;

  // D11 (v2.7) — mesma função do CartoesDeData: competência = mês de início
  // da quinzena, não o de pagamento. O fechamento sai no mês SEGUINTE a ela.
  const competencia = competenciaDaQuinzena({ periodoInicio });
  const dataFechamento = dataPagamentoDaCompetencia(proximaCompetencia(competencia), diaPagamentoMensal);

  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between gap-2">
        <h2 className="text-sm font-bold text-foreground">Mensalistas e encarregados</h2>
        <p className="text-[11px] text-muted-foreground">
          registro de presença — o salário sai no fechamento do mês
        </p>
      </div>

      <div className="space-y-2">
        {linhas.map(l => {
          // §6 — normaliza o que veio do servidor em Set<'YYYY-MM-DD'> antes de
          // comparar com a faixa de dias. A contagem (dias_registro/dias_falta,
          // COUNT(*) puro) já chegava certa; o que quebrava era comparar a
          // string 'YYYY-MM-DD' da faixa com o array bruto de `date` do SQL cru,
          // que não tem formato garantido sem o `::text` que a query já força
          // agora — isto aqui é a segunda camada de defesa, não a única.
          const registro = normalizarDatasISO(l.datas_registro);
          const falta = normalizarDatasISO(l.datas_falta);
          const ehQuinzenal = (l.forma_pagamento ?? "MENSAL") === "QUINZENAL";
          const adiantamento = l.adiantamento_valor ?? "0";
          const pago = l.adiantamento_status === "PAGO";
          const adicionais = adicionalPorFuncionario?.get(l.id);
          // CAIXA — só o adiantamento DESTA quinzena entra no total da linha.
          // Nunca o da competência: ele já saiu (ou vai sair) em outra data.
          const totalLinhaCentavos =
            paraCentavos(ehQuinzenal && l.adiantamento_id ? adiantamento : "0") + somarLinhasAdicional(adicionais);

          // Adiantamento da COMPETÊNCIA (qualquer quinzena do mês) — é ele que
          // responde "já tem?" e o que o fechamento do mês vai abater. Num mês
          // com duas quinzenas, na segunda o `adiantamento_valor` é nulo mesmo
          // com o do mês já pago na primeira.
          const temNaCompetencia = temAdiantamentoNaCompetencia(l);
          const adiantamentoDoMes = l.adiantamento_comp_valor ?? (l.adiantamento_id ? adiantamento : "0");

          // §4.3 — projeção do que ainda vem. Cálculo de EXIBIÇÃO: o valor real é
          // congelado no fechamento, pelo servidor. Se divergirem, manda o snapshot.
          const projecao = calcLiquidoFechamento({
            salarioFixo: l.salario_mensal,
            adiantamentosPagos: ehQuinzenal ? adiantamentoDoMes : "0",
            variavelApurada: 0,
            descontos: 0,
          });
          // Raiz do bug: sem adiantamento gerado ainda, `projecao` (acima) daria
          // o salário CHEIO no fechamento — cenário que só existiria se o
          // adiantamento nunca fosse gerado, o que não é o caso real. Projeta os
          // DOIS pagamentos que vão existir quando ele for gerado. Quem já tem o
          // da competência (mesmo pago em outra quinzena) não é "pendente".
          const projecaoPendente = (ehQuinzenal && !temNaCompetencia)
            ? calcProjecaoAdiantamentoPendente(l.salario_mensal, l.adiantamento_pct)
            : null;

          return (
            <div key={l.id} className="rounded-xl border border-border bg-card overflow-hidden">
              <div className="flex items-start justify-between gap-2 px-3 pt-2.5">
                <div className="min-w-0">
                  {/* Clicar no nome sempre leva à Ficha do mês (a resposta única
                      de "quanto essa pessoa recebe, e por quê") — mesmo link em
                      toda tela que mostra um nome (§ "Como se chega nela"). */}
                  <Link
                    href={`/equipe/ficha/${l.id}?competencia=${competencia}`}
                    state={origemAtual()}
                    className="text-sm font-semibold text-foreground truncate hover:underline block"
                  >
                    {nomeDe(l)}
                  </Link>
                  <p className="text-[11px] text-muted-foreground">
                    {plural(l.dias_registro, "dia", "dias")} de registro
                    {l.dias_falta > 0 && ` · ${plural(l.dias_falta, "falta", "faltas")}`}
                  </p>
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  <Badge variant="outline" className="text-[10px] font-normal">
                    {ehQuinzenal ? "Quinzenal" : "1×/mês"}
                  </Badge>
                  {/* §4 — fato histórico: tem diária MENSALISTA nesta quinzena, mas o
                      regime de hoje já mudou. Aparece aqui por causa do PASSADO, não
                      do presente — o marcador evita confundir os dois. */}
                  {l.era_mensalista && (
                    <Badge variant="outline" className="bg-atencao-suave text-atencao border-atencao/30 text-[9px]">
                      era mensalista nesta quinzena
                    </Badge>
                  )}
                </div>
              </div>

              {/* Faixa de dias — rolável no celular, porque 15 colunas não cabem. */}
              <div className="mt-2 overflow-x-auto px-3">
                <div className="flex gap-1 pb-1">
                  {dias.map(d => {
                    const temRegistro = registro.has(d);
                    const temFalta = falta.has(d);
                    return (
                      <div
                        key={d}
                        className={`shrink-0 w-9 rounded-md py-1 text-center text-[10px] leading-tight border ${
                          temFalta ? "bg-atencao-suave border-atencao/30 text-atencao"
                            : temRegistro ? "bg-sucesso-suave border-sucesso/30 text-sucesso"
                            : "bg-muted border-border text-muted-foreground/60"
                        }`}
                      >
                        <span className="block font-medium">{d.slice(8, 10)}</span>
                        {/* Nunca R$ aqui. */}
                        <span className="block">{temFalta ? "falta" : temRegistro ? "✓" : "–"}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Dinheiro: só as colunas da direita. */}
              <div className="mt-1 grid grid-cols-3 gap-px bg-muted border-t border-border">
                <div className="bg-card px-2 py-1.5">
                  <p className="text-[10px] uppercase text-muted-foreground">Adiantamento</p>
                  {/* Três estados, não dois: sai NESTA quinzena · já resolvido em
                      OUTRA quinzena do mês (ou dispensado) · realmente não gerado.
                      Só o primeiro é dinheiro desta data. */}
                  {ehQuinzenal && l.adiantamento_id ? (
                    <p className="text-sm font-semibold text-foreground">{formatarBRL(adiantamento)}</p>
                  ) : ehQuinzenal && l.adiantamento_comp_id ? (
                    <>
                      <p className="text-sm font-semibold text-muted-foreground">{formatarBRL(l.adiantamento_comp_valor)}</p>
                      <p className="text-[10px] text-muted-foreground">
                        {l.adiantamento_comp_status === "PAGO" ? "pago" : "previsto"} com a quinzena de{" "}
                        {formatarDataCurta(l.adiantamento_comp_quinzena_data_pagamento ?? l.adiantamento_comp_quinzena_fim)}
                      </p>
                    </>
                  ) : ehQuinzenal && l.dispensa_comp_id ? (
                    <p className="text-xs text-muted-foreground">não houve neste mês</p>
                  ) : ehQuinzenal ? (
                    <p className="text-xs text-muted-foreground">não gerado</p>
                  ) : (
                    <p className="text-xs text-muted-foreground">— recebe 1×/mês</p>
                  )}
                </div>
                <div className="bg-card px-2 py-1.5">
                  <p className="text-[10px] uppercase text-muted-foreground">Adicionais</p>
                  {adicionais?.length ? (
                    adicionais.map(linha => (
                      <div key={`${linha.tipo}-${linha.sinal}`}>
                        {/* Desconto nunca com "+": mesmo valor positivo, sentido oposto (046). */}
                        <p className={`text-sm font-semibold ${linha.sinal === "DESCONTO" ? "text-erro" : "text-sucesso"}`}>
                          {linha.sinal === "DESCONTO" ? "−" : "+"} {formatarBRL(paraReais(linha.totalCentavos))}
                        </p>
                        <p className="text-[10px] text-muted-foreground truncate">
                          {formatarResumoAdicional({
                            qtd: linha.qtd, kgTotal: linha.kgTotal,
                            totalValor: paraReais(linha.totalCentavos),
                            tipoLabel: "lançamento",
                          })}
                        </p>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-muted-foreground">—</p>
                  )}
                </div>
                <div className="bg-card px-2 py-1.5">
                  <p className="text-[10px] uppercase text-muted-foreground">Total</p>
                  <p className="text-sm font-bold text-foreground">
                    {formatarBRL(paraReais(totalLinhaCentavos))}
                  </p>
                </div>
              </div>

              {pago && (
                <p className="px-3 py-1 text-[10px] text-sucesso border-t border-border">
                  Adiantamento pago · competência {l.adiantamento_competencia}
                </p>
              )}

              {/* §4.3 — faixa roxa da projeção do fechamento. Não faz sentido para quem
                  já não é mensalista hoje (l.era_mensalista): não vai ter fechamento
                  mensal nenhum, a linha só existe aqui pelo fato histórico da diária. */}
              {!l.era_mensalista && (
                projecaoPendente ? (
                  <div className="bg-atencao-suave px-3 py-1.5 text-[11px] text-atencao border-t border-atencao/30">
                    adiantamento não gerado · quando gerar: <strong>{formatarBRL(projecaoPendente.valorQuinzena)}</strong> na
                    quinzena + <strong>{formatarBRL(projecaoPendente.valorFechamento)}</strong> no fechamento de {formatarDataCurta(dataFechamento)}
                  </div>
                ) : (
                  <div className="bg-muted px-3 py-1.5 text-[11px] text-foreground border-t border-border">
                    No fechamento de {formatarDataCurta(dataFechamento)}: <strong>{formatarBRL(projecao)}</strong>
                    {ehQuinzenal
                      ? Number(l.variavel_max_mensal ?? 0) > 0
                        ? <> + variável (teto {formatarBRL(l.variavel_max_mensal)})</>
                        : null
                      : <> — salário inteiro</>}
                  </div>
                )
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
